// Utility functions for React app
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString();
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString();
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

import { auth } from "@/services/firebase";

export const fetcher = async (url: string) => {
  // Get the current user's auth token
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Add Firebase auth token if user is signed in
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      headers["Authorization"] = `Bearer ${token}`;
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
  }

  // Use the API base URL from environment variables
  const baseUrl = import.meta.env.VITE_API_URL
  const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

  const res = await fetch(fullUrl, {
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const error = new Error(
      "An error occurred while fetching the data.",
    ) as ApplicationError;

    try {
      error.info = await res.json();
    } catch {
      error.info = res.statusText;
    }
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function getLocalStorage(key: string) {
  if (typeof window !== "undefined") {
    return JSON.parse(localStorage.getItem(key) || "[]");
  }
  return [];
}

interface Document {
  createdAt: Date | any;
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  const ts = documents[index].createdAt;
  if (ts instanceof Date) return ts;
  // Firestore Timestamp has a toDate() method
  if (typeof (ts as any).toDate === "function") return (ts as any).toDate();
  // Fallback: assume it's milliseconds since epoch or ISO string
  return new Date(ts as unknown as string | number);
}
